import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, Button, Input, Label, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getHomeAdBanners, addHomeAdBanner, deleteHomeAdBanner } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const HomeAdBanner = () => {
  document.title = "Home Ad Space - Dashboard";
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await getHomeAdBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to load ad banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const toggle = () => {
    setModal(!modal);
    setLinkUrl("");
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error("Please select an image");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("adBannerImage", imageFile);
      formData.append("link_url", linkUrl);
      try {
        const raw = localStorage.getItem("authUser");
        let hasToken = false;
        if (raw) {
          const u = JSON.parse(raw);
          hasToken = !!(u.token || u.accessToken);
        }
        console.log("[HomeAdBanner] Upload start", {
          fileName: imageFile?.name,
          fileSize: imageFile?.size,
          hasAuthUserToken: hasToken,
        });
      } catch (e) {
        console.warn("[HomeAdBanner] Could not read auth state", e);
      }
      await addHomeAdBanner(formData);
      toast.success("Ad banner added successfully");
      toggle();
      fetchBanners();
    } catch (err) {
      console.error("[HomeAdBanner] Upload failed — full error object:", err);
      const hint =
        err?.code === "TokenExpiredError"
          ? " Session expired — please log out and log in again."
          : err?.code === "JsonWebTokenError"
            ? " Bad token — log in again (check server JWT_SECRET matches login)."
            : err?.detail
              ? ` (${err.detail})`
              : "";
      toast.error(
        `${err?.error || err?.message || "Failed to add ad banner"}${hint}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this ad banner?")) return;
    try {
      await deleteHomeAdBanner(id);
      toast.success("Ad banner deleted");
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || "Failed to delete ad banner");
    }
  };

  const getImageUrl = (banner) => {
    if (!banner.image) return null;
    if (banner.image.startsWith("http")) return banner.image;
    const base = (API_BASE || "").replace("/api", "");
    return `${base}${banner.image.startsWith("/") ? "" : "/"}${banner.image}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Home Ad Space" pageTitle="Website" />
        <Row>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Ad Banners (Home Page Right Side)</h5>
                <Button color="primary" onClick={toggle}>
                  <i className="ri-add-line align-bottom me-1"></i> Upload Ad Banner
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner /> Loading...
                </div>
              ) : banners.length === 0 ? (
                <p className="text-muted">No ad banners yet. Upload your first ad banner.</p>
              ) : (
                <Row>
                  {banners.map((b) => (
                    <Col md={6} lg={4} key={b.id} className="mb-3">
                      <div className="border rounded p-2 position-relative">
                        <img
                          src={getImageUrl(b)}
                          alt="Ad Banner"
                          className="img-fluid rounded"
                          style={{ maxHeight: 200, objectFit: "cover", width: "100%" }}
                        />
                        {b.link_url && (
                          <div className="mt-2 small text-muted">Link: {b.link_url}</div>
                        )}
                        <Button
                          color="danger"
                          size="sm"
                          className="mt-2"
                          onClick={() => handleDelete(b.id)}
                        >
                          <i className="ri-delete-bin-line"></i> Delete
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </CardBody>
          </Card>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Upload Home Ad Banner</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Label>Image <span className="text-danger">*</span></Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
                required
              />
            </div>
            <div className="mb-3">
              <Label>Link URL (optional)</Label>
              <Input
                type="url"
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggle}>Cancel</Button>
              <Button type="submit" color="primary" disabled={submitting}>
                {submitting ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default HomeAdBanner;
